// What the leader's screens are drawn from: the floor's score, each flowline's
// standing, who needs them, and the day hour by hour.
//
// Everything here is a view over `useFloorPairs` — one row per work centre per
// flowline with the word for it decided once, in `lib/stationTone` — and the
// two lists that are not stations at all: unanswered andons and the quality
// alerts holding pods back.

import { useMemo } from 'react';
import type { AlertVM, AndonVM, DepartmentVM, ScoreVM, WorkcentreVM } from '@/api/types';
import type { NeedCardVM } from '@/components/home/NeedCard';
import { useT } from '@/i18n/useT';
import type { Translations } from '@/i18n/strings';
import { fmtLocalTime } from '@/lib/pace';
import type { StationTone } from '@/lib/stationTone';
import { TONE_RANK } from '@/lib/stationTone';
import { predecessorsOf } from '@/lib/topology';
import { useStore } from '@/store';
import {
  type FloorPair,
  andonHref,
  useCalendar,
  useDepartments,
  useDoneToday,
  useEolWorkcentre,
  useFloorAlerts,
  useFloorPairs,
  useFlowlineShort,
  useFlowlineTopology,
  useFlowlines,
  useScoreFor,
  useUnacknowledgedAndons,
  useWorkcentres,
} from './index';
import { paceWindow, windowAt } from '@/lib/pace';

// ── the score and the flowline rows ────────────────────────────────────────

/**
 * The floor's headline: End-of-Line QC's day, per flowline and rolled up.
 *
 * A pod counts for the floor when it leaves it, so the leader's number is the
 * last station's, not a sum over sixty. Scoped by the same flowline chips as
 * everything else on the screen.
 */
export function useFloorScore(): ReturnType<typeof useScoreFor> {
  const eol = useEolWorkcentre();
  const fl = useStore((s) => s.flowlineId);
  return useScoreFor(eol?.id ?? null, fl);
}

export interface FloorLineRow {
  flowlineId: number;
  short: string;
  name: string;
  done: number;
  target: number | null;
  dueNow: number | null;
  delta: number | null;
  stopped: number;
  starved: number;
  behind: number;
  /** The pairs on this line that need somebody, worst first. */
  exceptions: FloorPair[];
}

/** One row per flowline in scope: EOL QC's score and how many stations need help. */
export function useFloorLines(flowlineIds: number[]): { lines: FloorLineRow[]; isPending: boolean } {
  const score = useFloorScore();
  const { pairs, isPending } = useFloorPairs(flowlineIds);
  const flowlines = useFlowlines();
  const short = useFlowlineShort();

  const lines = useMemo(() => {
    return flowlineIds.map((flId) => {
      const line = score.data?.lines.find((l) => l.flowlineId === flId);
      const mine = pairs.filter((p) => p.state.flowlineId === flId);
      const exceptions = mine
        .filter((p) => p.tone.kind === 'danger' || p.tone.kind === 'warn')
        .sort((a, b) => TONE_RANK[a.tone.kind] - TONE_RANK[b.tone.kind] || a.workcentre.sequence - b.workcentre.sequence);
      const stopped = exceptions.filter((p) => p.tone.word === 'stopped').length;
      const starved = exceptions.filter((p) => p.tone.word === 'starved').length;
      return {
        flowlineId: flId,
        short: short(flId),
        name: flowlines.data?.find((f) => f.id === flId)?.name ?? '',
        done: line?.done ?? 0,
        target: line?.target ?? null,
        dueNow: line?.dueNow ?? null,
        delta: line?.delta ?? null,
        stopped,
        starved,
        behind: exceptions.length - stopped - starved,
        exceptions,
      } satisfies FloorLineRow;
    });
  }, [flowlineIds, score.data, pairs, flowlines.data, short]);

  return { lines, isPending: isPending || score.isPending };
}

// ── needs you ──────────────────────────────────────────────────────────────

export interface StarvedRow {
  pair: FloorPair;
  /** The station before it on the route, when the route names one. */
  feeder: WorkcentreVM | null;
}

/** The starved pairs in scope, and who feeds each — the fix is upstream. */
export function useStarvedRows(flowlineIds: number[], limit = 3, enabled = true): StarvedRow[] {
  const { pairs } = useFloorPairs(flowlineIds);
  const topology = useFlowlineTopology(flowlineIds, enabled);
  const workcentres = useWorkcentres();
  return useMemo(() => {
    const wcById = new Map((workcentres.data ?? []).map((w) => [w.id, w]));
    return pairs
      .filter((p) => p.tone.word === 'starved')
      .sort((a, b) => a.workcentre.sequence - b.workcentre.sequence)
      .slice(0, limit)
      .map((pair) => {
        const before = predecessorsOf(topology, pair.workcentre.id, pair.state.flowlineId)[0];
        return { pair, feeder: before ? (wcById.get(before.workcentreId) ?? null) : null };
      });
  }, [pairs, topology, workcentres.data, limit]);
}

/**
 * The leader's "needs you": worst first — stopped, then starved, then owed.
 *
 * Unanswered andons on the lines in scope (an andon with no line is on every
 * line), the starved stations, then the quality alerts holding a pod's
 * sign-off. Each card carries its verb: Go, Acknowledge (which hands to the
 * Andon app, the owner of the clock), Go to the feeder, Close.
 */
export function useFloorNeeds(
  flowlineIds: number[],
  actions: {
    goToStation: (workcentreId: number, name: string) => void;
    openAndon: (andonId: number) => void;
    openQuality: (alertId: number) => void;
    takeAndon?: (andon: AndonVM) => void;
  },
  // The floor's reads belong to a leader on the floor. An operator's badges
  // come from their own station read; running the floor's topology and alert
  // reads for every phone was what polled production for a model it lacks.
  enabled = true,
): { items: NeedCardVM[]; andons: AndonVM[]; starved: StarvedRow[]; quality: AlertVM[]; isPending: boolean } {
  const t = useT();
  const andonAppUrl = useStore((s) => s.andonAppUrl);
  const andonPreview = useStore((s) => s.andonCaps.preview);
  const unack = useUnacknowledgedAndons(enabled);
  const starved = useStarvedRows(flowlineIds, 3, enabled);
  const blocking = useFloorAlerts(enabled, true);
  const short = useFlowlineShort();

  const andons = useMemo(
    () =>
      (unack.data ?? []).filter((a) => a.flowlineId == null || flowlineIds.includes(a.flowlineId)),
    [unack.data, flowlineIds],
  );
  const quality = blocking.data ?? [];

  const items = useMemo<NeedCardVM[]>(
    () => [
      ...andons.map((a) => andonNeedCard(a, t, andonAppUrl, short(a.flowlineId), actions, true, null, andonPreview)),
      ...starved.map(({ pair, feeder }) => ({
        key: `starved:${pair.workcentre.id}:${pair.state.flowlineId}`,
        tag: t.starvedTagT,
        tagTone: 'warn' as const,
        urgent: true,
        title: `${pair.workcentre.name} ${t.hasNothingReadyTitleT}`,
        meta: [
          short(pair.state.flowlineId),
          `${pair.state.waiting} ${t.podsWaitingUpstreamT}`,
          `${t.fedByT} ${feeder?.name ?? '—'}`,
        ].join(' · '),
        onOpen: () => actions.goToStation(pair.workcentre.id, pair.workcentre.name),
        verb: {
          label: `${t.goToT} ${feeder?.name ?? pair.workcentre.name}`,
          onClick: () =>
            feeder
              ? actions.goToStation(feeder.id, feeder.name)
              : actions.goToStation(pair.workcentre.id, pair.workcentre.name),
        },
      })),
      ...quality.map((q) => qualityNeedCard(q, t, true, actions.openQuality)),
    ],
    [andons, starved, quality, t, andonAppUrl, short, actions, andonPreview],
  );

  return { items, andons, starved, quality, isPending: unack.isPending || blocking.isPending };
}

/**
 * An andon as a card.
 *
 * The verb is a link while the Andon app owns acknowledging; with the preview
 * on it is "Take it", straight into the ETA sheet where the screen hosts one
 * (frame v2.12 script L853: `onVerb: takeAndon(a)`), and into this app's own
 * thread at /alert/<id> — where the same sheet is one tap away — where it
 * does not. `andonHref` stays for the preview-off path.
 */
export function andonNeedCard(
  a: AndonVM,
  t: Translations,
  andonAppUrl: string | null,
  flowlineShort: string,
  actions: { goToStation: (workcentreId: number, name: string) => void; openAndon: (andonId: number) => void; takeAndon?: (andon: AndonVM) => void },
  leader: boolean,
  currentWorkcentreId: number | null = null,
  andonPreview = false,
): NeedCardVM {
  const answered = a.state !== 'new';
  const card: NeedCardVM = {
    key: `andon:${a.id}`,
    tag: '★'.repeat(a.stars),
    tagTone: a.stars === 3 ? 'danger' : 'neutral',
    urgent: a.stars === 3 && !answered,
    title: a.title,
    meta: [
      a.ref,
      a.workcentreName,
      flowlineShort,
      a.raisedByName,
      fmtLocalTime(a.raisedAt),
      a.overdue && !answered ? t.slaMissedT : null,
      answered ? `${t.answeredByT} ${a.ownerName || '—'}` : t.unansweredT,
    ]
      .filter(Boolean)
      .join(' · '),
    onOpen: () => actions.openAndon(a.id),
  };
  if (a.workcentreId != null && a.workcentreId !== currentWorkcentreId) {
    card.go = { label: t.goToWorkCentreT, onClick: () => actions.goToStation(a.workcentreId!, a.workcentreName) };
  }
  if (leader && !answered) {
    const take = actions.takeAndon;
    card.verb = andonPreview
      ? { label: t.verbTakeItT, onClick: () => (take ? take(a) : actions.openAndon(a.id)) }
      : { label: t.ackT, href: andonHref(andonAppUrl, a.id) };
  }
  return card;
}

/** A quality alert as a card. Closing is a sheet, so the verb opens it. */
export function qualityNeedCard(
  q: AlertVM,
  t: Translations,
  canClose: boolean,
  openQuality: (alertId: number) => void,
): NeedCardVM {
  const card: NeedCardVM = {
    key: `quality:${q.id}`,
    tag: 'QA',
    tagTone: 'neutral',
    urgent: false,
    title: q.title,
    meta: [q.qa, q.mo, q.team, fmtLocalTime(q.createdAt), q.preventMoSignoff ? t.blocksSignoffT : null]
      .filter(Boolean)
      .join(' · '),
    onOpen: () => openQuality(q.id),
  };
  if (canClose) card.verb = { label: t.closeEllipsisT, onClick: () => openQuality(q.id) };
  return card;
}

// ── the Work centres view ──────────────────────────────────────────────────

export interface DepartmentGroup {
  department: DepartmentVM | null;
  name: string;
  /** Work centres in this department with a pair in scope, in sequence. */
  workcentres: Array<{ workcentre: WorkcentreVM; pairs: FloorPair[]; worst: StationTone }>;
  needYou: number;
}

/**
 * The floor grouped by department, every line of each work centre inside its
 * one card.
 *
 * Departments come from `hr.department` and only the ones a work centre in
 * scope belongs to appear — the whole company tree is in that table and a
 * leader does not read the floor by Finance. There is no order on the
 * department model, so groups run in the order the floor does: by the lowest
 * work-centre sequence each holds.
 */
export function useDepartmentGroups(
  flowlineIds: number[],
  departmentId: number | null,
): { groups: DepartmentGroup[]; departments: DepartmentVM[]; isPending: boolean } {
  const { pairs, isPending } = useFloorPairs(flowlineIds);
  const departments = useDepartments();

  return useMemo(() => {
    const byWc = new Map<number, { workcentre: WorkcentreVM; pairs: FloorPair[] }>();
    for (const p of pairs) {
      const entry = byWc.get(p.workcentre.id) ?? { workcentre: p.workcentre, pairs: [] };
      entry.pairs.push(p);
      byWc.set(p.workcentre.id, entry);
    }
    const deptById = new Map((departments.data ?? []).map((d) => [d.id, d]));
    const groupsById = new Map<number | null, DepartmentGroup>();
    for (const entry of byWc.values()) {
      const key = entry.workcentre.departmentId;
      const dept = key == null ? null : (deptById.get(key) ?? null);
      // The department's own name, not its path through the company tree:
      // "Welding", not "Production / Value Stream 1 / Welding". The path is
      // longer than a phone is wide, and the leader already knows the plant.
      const g = groupsById.get(key) ?? {
        department: dept,
        name: dept?.name || entry.workcentre.departmentName || '—',
        workcentres: [],
        needYou: 0,
      };
      const worst = [...entry.pairs].sort(
        (a, b) => TONE_RANK[a.tone.kind] - TONE_RANK[b.tone.kind] || b.tone.behind - a.tone.behind,
      )[0]!.tone;
      g.workcentres.push({
        workcentre: entry.workcentre,
        pairs: [...entry.pairs].sort((a, b) => a.state.flowlineId - b.state.flowlineId),
        worst,
      });
      if (worst.kind === 'danger' || worst.kind === 'warn') g.needYou += 1;
      groupsById.set(key, g);
    }
    const all = [...groupsById.values()].map((g) => ({
      ...g,
      workcentres: g.workcentres.sort((a, b) => a.workcentre.sequence - b.workcentre.sequence),
    }));
    all.sort(
      (a, b) =>
        Math.min(...a.workcentres.map((w) => w.workcentre.sequence)) -
        Math.min(...b.workcentres.map((w) => w.workcentre.sequence)),
    );
    const used = all.map((g) => g.department).filter((d): d is DepartmentVM => d != null);
    return {
      groups: departmentId == null ? all : all.filter((g) => g.department?.id === departmentId),
      departments: used,
      isPending,
    };
  }, [pairs, departments.data, departmentId, isPending]);
}

// ── the day, hour by hour ──────────────────────────────────────────────────

export interface EolHourLine {
  flowlineId: number;
  short: string;
  done: number;
  target: number | null;
  bars: Array<{ hour: number; count: number }>;
}

/**
 * Completions at End-of-Line QC by hour, one strip per flowline in scope.
 *
 * The hours run across EOL QC's own shift, from the first working hour to the
 * last, so a quiet hour between two busy ones shows as the gap it is. Where the
 * calendar cannot say, the hours something happened in bound the axis.
 */
export function useEolHourLines(flowlineIds: number[]): EolHourLine[] {
  const eol = useEolWorkcentre();
  const score = useFloorScore();
  const done = useDoneToday(eol?.id ?? null, true);
  const calendar = useCalendar(eol?.calendarId ?? null);
  const short = useFlowlineShort();

  return useMemo(() => {
    const cal = calendar.data ?? null;
    const rows = (done.data ?? []).filter((r) => r.hour != null);
    let from: number | null = null;
    let to: number | null = null;
    if (cal && paceWindow(cal).scheduled) {
      for (let hour = 0; hour < 24; hour += 1) {
        if (windowAt(cal, (hour + 1) * 60).elapsedMinutes > windowAt(cal, hour * 60).elapsedMinutes) {
          if (from == null) from = hour;
          to = hour;
        }
      }
    }
    for (const r of rows) {
      from = from == null ? r.hour! : Math.min(from, r.hour!);
      to = to == null ? r.hour! : Math.max(to, r.hour!);
    }
    const hours = from == null || to == null ? [] : Array.from({ length: to - from + 1 }, (_, i) => from! + i);
    return flowlineIds.map((flId) => {
      const line = score.data?.lines.find((l) => l.flowlineId === flId);
      return {
        flowlineId: flId,
        short: short(flId),
        done: line?.done ?? 0,
        target: line?.target ?? null,
        bars: hours.map((hour) => ({
          hour,
          count: rows.filter((r) => r.flowlineId === flId && r.hour === hour).reduce((s, r) => s + r.count, 0),
        })),
      };
    });
  }, [flowlineIds, done.data, calendar.data, score.data, short]);
}
